import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CircleCheckBold } from "@/components/icons/CircleCheckBold";
import { CircleAlertBold } from "@/components/icons/CircleAlertBold";
import { CircleXBold } from "@/components/icons";

type TextareaStatus = "empty" | "filled" | "focused";

// Context for TextareaGroup
interface TextareaGroupContextValue {
  status: TextareaStatus;
  error: boolean;
  disabled: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  setTextareaRef: (node: HTMLTextAreaElement | null) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  hasValue: boolean;
  setHasValue: React.Dispatch<React.SetStateAction<boolean>>;
  charCount: number;
  setCharCount: React.Dispatch<React.SetStateAction<number>>;
  maxLength?: number;
}

const TextareaGroupContext =
  React.createContext<TextareaGroupContextValue | null>(null);

const useTextareaGroupContext = () => {
  return React.useContext(TextareaGroupContext);
};

// TextareaGroup Wrapper 스타일 (border + padding)
const textareaGroupWrapperVariants = cva(
  "flex flex-col gap-2.5 bg-static-white transition-shadow px-4 py-3 rounded-xl",
  {
    variants: {
      status: {
        empty: "",
        filled: "",
        focused: "",
      },
      error: {
        true: "",
        false: "",
      },
      disabled: {
        true: "bg-fills-neutral cursor-not-allowed",
        false: "",
      },
    },
    compoundVariants: [
      // Normal states
      {
        error: false,
        disabled: false,
        status: "empty",
        className: "inset-border-solid-normal",
      },
      {
        error: false,
        disabled: false,
        status: "filled",
        className: "inset-border-solid-normal",
      },
      {
        error: false,
        disabled: false,
        status: "focused",
        className: "inset-border-focus",
      },
      // Error states
      {
        error: true,
        disabled: false,
        status: "empty",
        className: "inset-border-error",
      },
      {
        error: true,
        disabled: false,
        status: "filled",
        className: "inset-border-error",
      },
      {
        error: true,
        disabled: false,
        status: "focused",
        className: "inset-border-error-focus",
      },
      // Disabled state
      { disabled: true, className: "inset-border-solid-normal" },
    ],
    defaultVariants: {
      status: "empty",
      error: false,
      disabled: false,
    },
  },
);

// TextareaGroup Component
interface TextareaGroupProps extends React.ComponentProps<"div"> {
  error?: boolean;
  disabled?: boolean;
  maxLength?: number;
}

function TextareaGroup({
  className,
  error = false,
  disabled = false,
  maxLength,
  children,
  ...props
}: TextareaGroupProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(false);
  const [charCount, setCharCount] = React.useState(0);

  const setTextareaRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      (
        textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
      ).current = node;
    },
    [],
  );

  const status: TextareaStatus = React.useMemo(() => {
    if (isFocused) return "focused";
    if (hasValue) return "filled";
    return "empty";
  }, [isFocused, hasValue]);

  const contextValue: TextareaGroupContextValue = {
    status,
    error,
    disabled,
    textareaRef,
    setTextareaRef,
    isFocused,
    setIsFocused,
    hasValue,
    setHasValue,
    charCount,
    setCharCount,
    maxLength,
  };

  return (
    <TextareaGroupContext.Provider value={contextValue}>
      <div
        data-slot="textarea-group"
        data-error={error}
        data-disabled={disabled}
        className={cn(
          "flex w-full flex-col",
          textareaGroupWrapperVariants({ status, error, disabled }),
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </TextareaGroupContext.Provider>
  );
}

// Textarea variants (standalone용)
const textareaVariants = cva(
  [
    "w-full min-w-0 resize-none overflow-y-auto bg-static-white transition-shadow outline-none max-h-42",
    "placeholder:text-labels-assistive",
    "disabled:bg-fills-neutral disabled:cursor-not-allowed disabled:text-labels-alternative",
  ],
  {
    variants: {
      size: {
        large: "px-4 py-3 rounded-xl typo-body-1-medium",
        small: "px-3 py-2 rounded-lg typo-body-2-medium",
      },
    },
    defaultVariants: {
      size: "large",
    },
  },
);

// 텍스트 색상 결정 함수
const getTextareaTextColor = (
  status: TextareaStatus,
  disabled: boolean,
): string => {
  if (disabled && status === "filled") return "text-labels-alternative";
  if ((status === "filled" || status === "focused") && !disabled)
    return "text-labels-primary";
  return "text-labels-assistive";
};

// Textarea Component
interface TextareaProps
  extends
    Omit<React.ComponentProps<"textarea">, "size">,
    VariantProps<typeof textareaVariants> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, size, value, onChange, onFocus, onBlur, maxLength, ...props },
    ref,
  ) => {
    const context = useTextareaGroupContext();
    const isInGroup = !!context;

    // Standalone용 로컬 ref (항상 선언)
    const localRef = React.useRef<HTMLTextAreaElement>(null);

    const status = context?.status ?? "empty";
    const disabled = context?.disabled ?? props.disabled ?? false;
    const setTextareaRef = context?.setTextareaRef;
    const setIsFocused = context?.setIsFocused;
    const setHasValue = context?.setHasValue;
    const setCharCount = context?.setCharCount;
    const contextMaxLength = context?.maxLength ?? maxLength;

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused?.(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused?.(false);
      onBlur?.(e);
    };

    // 높이 자동 조절 함수
    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = e.target;
      const newValue = textarea.value;
      setHasValue?.(!!newValue);
      setCharCount?.(newValue.length);
      adjustTextareaHeight(textarea);
      onChange?.(e);
    };

    // onInput으로 uncontrolled 상태에서도 높이 조절
    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      setHasValue?.(!!textarea.value);
      setCharCount?.(textarea.value.length);
      adjustTextareaHeight(textarea);
    };

    // Standalone용 높이 조절
    const handleStandaloneInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    // value prop이 변경될 때 hasValue와 charCount 업데이트 + 높이 조절
    React.useLayoutEffect(() => {
      if (isInGroup) {
        const strValue = typeof value === "string" ? value : "";
        setHasValue?.(!!strValue);
        setCharCount?.(strValue.length);

        // 브라우저가 레이아웃을 계산한 후 다음 프레임에서 높이 조정
        const textarea = context?.textareaRef.current;
        if (textarea) {
          const frameId = requestAnimationFrame(() => {
            textarea.style.height = "auto";
            textarea.style.height = `${textarea.scrollHeight}px`;
          });
          return () => cancelAnimationFrame(frameId);
        }
      } else {
        // Standalone용 초기 렌더링 및 value 변경 시 높이 조절
        const textarea = localRef.current;
        if (textarea) {
          const frameId = requestAnimationFrame(() => {
            textarea.style.height = "auto";
            textarea.style.height = `${textarea.scrollHeight}px`;
          });
          return () => cancelAnimationFrame(frameId);
        }
      }
    }, [value, setHasValue, setCharCount, isInGroup, context?.textareaRef]);

    // TextareaGroup 내부에서 사용될 때
    if (isInGroup) {
      return (
        <textarea
          ref={(node) => {
            // 두 ref 모두 연결
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
            setTextareaRef?.(node);
          }}
          data-slot="textarea"
          disabled={disabled}
          value={value}
          maxLength={contextMaxLength}
          onChange={handleChange}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          rows={1}
          className={cn(
            "bg-transparent outline-none min-w-0 resize-none overflow-y-auto max-h-42",
            "placeholder:text-labels-assistive",
            "typo-body-1-medium",
            getTextareaTextColor(status, disabled),
            disabled && "cursor-not-allowed",
            className,
          )}
          {...props}
        />
      );
    }

    // Standalone으로 사용될 때
    return (
      <textarea
        ref={(node) => {
          // 두 ref 모두 연결
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
          localRef.current = node;
        }}
        data-slot="textarea"
        value={value}
        maxLength={maxLength}
        onChange={onChange}
        onInput={handleStandaloneInput}
        onFocus={onFocus}
        onBlur={onBlur}
        rows={1}
        className={cn(
          textareaVariants({ size }),
          "inset-border-solid-normal",
          "focus:inset-border-focus",
          "aria-invalid:inset-border-error",
          "aria-invalid:focus:inset-border-error-focus",
          "text-labels-primary",
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

// TextareaFooter Component (카운터 + 아이콘)
interface TextareaFooterProps {
  className?: string;
  showCounter?: boolean;
  showIcon?: boolean;
  customIcon?: React.ReactNode;
}

function TextareaFooter({
  className,
  showCounter = true,
  showIcon = true,
  customIcon,
}: TextareaFooterProps) {
  const context = useTextareaGroupContext();

  if (!context) {
    console.warn("TextareaFooter must be used within TextareaGroup");
    return null;
  }

  const {
    status,
    error,
    disabled,
    charCount,
    maxLength,
    textareaRef,
    setHasValue,
    setCharCount,
  } = context;

  const iconSize = 24;

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!textareaRef.current) return;

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      "value",
    )?.set;
    nativeInputValueSetter?.call(textareaRef.current, "");

    const event = new Event("input", { bubbles: true });
    textareaRef.current.dispatchEvent(event);

    setHasValue(false);
    setCharCount(0);
    textareaRef.current.focus();
  };

  const renderIcon = () => {
    if (!showIcon) return null;
    if (customIcon) return <>{customIcon}</>;

    // focused 상태: X 버튼 (clear)
    if (status === "focused") {
      return (
        <button
          type="button"
          onMouseDown={handleClear}
          className="shrink-0 cursor-pointer flex items-center"
          tabIndex={-1}
        >
          <CircleXBold size={iconSize} color="fills-strong" />
        </button>
      );
    }

    // error 상태: 에러 아이콘
    if (error) {
      return (
        <div className="shrink-0 flex items-center">
          <CircleAlertBold size={iconSize} color="status-urgent" />
        </div>
      );
    }

    // filled 상태 (disabled 아님): 체크 아이콘
    if (status === "filled" && !disabled) {
      return (
        <div className="shrink-0 flex items-center">
          <CircleCheckBold size={iconSize} color="primary-normal" />
        </div>
      );
    }

    // empty 상태: X 아이콘 (비활성)
    return (
      <div className="shrink-0 flex items-center">
        <CircleXBold size={iconSize} color="fills-strong" />
      </div>
    );
  };

  return (
    <div
      data-slot="textarea-footer"
      className={cn("flex items-center justify-between", className)}
    >
      {showCounter && maxLength ? (
        <span className="typo-caption-1-medium text-labels-assistive tracking-[0.12px]">
          {charCount}/{maxLength.toLocaleString()}
        </span>
      ) : (
        <span />
      )}
      {renderIcon()}
    </div>
  );
}

// displayName 설정
TextareaGroup.displayName = "TextareaGroup";
TextareaFooter.displayName = "TextareaFooter";

export {
  Textarea,
  TextareaGroup,
  TextareaFooter,
  textareaVariants,
  textareaGroupWrapperVariants,
};
